/**
 * Dashboard config: next steps links and workflow steps per goal.
 * Change here when adding new goals or updating workflow copy.
 */
import type { LucideIcon } from 'lucide-react';
import { Building, Home, Users } from 'lucide-react';

/** Goal keys the Dashboard workflow/next-steps cards key off (a subset of FactFindGoals's boolean fields). */
export type DashboardGoalKey = 'aged_care' | 'home_care' | 'retirement_village' | 'downsize_home' | 'granny_flat';

export interface NextStepConfigItem {
    text: string;
    link: string;
    page: string;
    goal: DashboardGoalKey;
}

export const NEXT_STEPS_CONFIG: NextStepConfigItem[] = [
    { text: "Review The Financial Impacts And Options Around", link: "Aged Care", page: "AgedCarePage", goal: "aged_care" },
    { text: "Review The Financial Impacts And Options Around", link: "Home Care", page: "HomeCarePage", goal: "home_care" },
    { text: "Review The Financial Impacts And Options Around", link: "Retirement Village", page: "RetirementVillagePage", goal: "retirement_village" },
    { text: "Review The Financial Impacts And Options Around", link: "Downsizing", page: "DownsizingPage", goal: "downsize_home" },
    { text: "Review The Financial Impacts And Options Around", link: "Granny Flat", page: "GrannyFlatPage", goal: "granny_flat" }
];

export interface WorkflowStepConfig {
    id: string;
    name: string;
    description: string;
    /**
     * The documents this step opens — ids from features/forms/actionPlanDocuments.ts, each of which
     * builds something real (a pre-filled form, a checklist, a page in the app). Never a bare label:
     * a chip with nothing behind it is how the old plan promised "pre-filled" forms it never made.
     */
    forms: string[];
    /** Shown greyed as "coming soon" — something we intend to offer but cannot yet (owner, 3 Oct 2026). */
    comingSoon?: string[];
    estimatedTime: string;
    status: string;
}

export interface WorkflowConfig {
    title: string;
    icon: LucideIcon;
    steps: WorkflowStepConfig[];
}

/** Keyed by the same goal ids that drive the "active workflows" filter in WorkflowCard.tsx.
 *  Every DashboardGoalKey needs an entry, or a client with that goal sees no journey in
 *  "Your Action Plan" — which is how a downsize_home + retirement_village client came to see
 *  only the Retirement Village journey. */
export const WORKFLOW_CONFIGS: Record<DashboardGoalKey, WorkflowConfig> = {
    /*
     * REWORKED 3 Oct 2026 (owner). The means assessment now comes BEFORE applying to homes, because
     * homes ask for the fee advice letter it produces. "Facility Application" and "Admission forms"
     * were the same act seen twice — each home has its own application, then its own agreement — so
     * they are one step; we hold no home's forms, so the pre-filled version is marked coming soon.
     */
    aged_care: {
        title: 'Aged Care Journey',
        icon: Building,
        steps: [
            { id: 'acat_assessment', name: 'Aged care needs assessment', description: 'Apply through My Aged Care, online or by phone. An assessor then visits to work out the care you are eligible for.', forms: ['assessment_application'], estimatedTime: '20 minutes to apply', status: 'pending' },
            { id: 'facility_research', name: 'Find and visit homes', description: 'Shortlist aged care homes and visit them. Ask each for its room prices, its waiting list and how to apply — My Aged Care lists every home.', forms: [], estimatedTime: '2-4 weeks', status: 'pending' },
            { id: 'sa457_form', name: 'Means assessment', description: 'Services Australia works out what you will pay towards your care. Homes ask for the letter it produces, so do it before applying.', forms: ['means_assessment'], estimatedTime: '~10 min (pre-filled)', status: 'pending' },
            { id: 'facility_application', name: 'Apply and accept a place', description: "Each home has its own application form. When you are offered a place you sign the home's agreement, which covers your care, your room and what you pay — have it checked before you sign.", forms: [], comingSoon: ['Pre-filled home applications'], estimatedTime: '1-4 weeks', status: 'pending' },
            { id: 'move_in_preparation', name: 'Move in', description: 'Pack what the home allows, bring the papers it needs, and tell people you have moved.', forms: ['move_in_checklist'], estimatedTime: '1-2 weeks', status: 'pending' }
        ]
    },
    home_care: {
        title: 'Home Care Journey',
        icon: Home,
        steps: [
            { id: 'acat_assessment', name: 'Aged care needs assessment', description: 'Apply through My Aged Care for an assessment for Support at Home. An assessor visits to work out the services you are eligible for.', forms: ['assessment_application_home'], estimatedTime: '20 minutes to apply', status: 'pending' },
            { id: 'sah_means_form', name: 'Contribution assessment', description: 'Services Australia works out what you will contribute towards your Support at Home services.', forms: ['means_assessment_home'], estimatedTime: '~10 min (pre-filled)', status: 'pending' },
            { id: 'package_assignment', name: 'Wait for funding', description: 'After your assessment you join the national queue. You will get a letter when your funding is assigned.', forms: [], estimatedTime: '3-6 months', status: 'pending' },
            { id: 'provider_selection', name: 'Choose a provider', description: 'Compare providers — each publishes its prices on My Aged Care — then choose one and sign its home care agreement.', forms: [], estimatedTime: '1-2 weeks', status: 'pending' },
            { id: 'care_planning', name: 'Agree your care plan', description: 'Your provider works out a care plan and budget with you. Check it matches the services you modelled in the Service Builder.', forms: [], estimatedTime: '2-4 weeks', status: 'pending' }
        ]
    },
    retirement_village: {
        title: 'Retirement Village Journey',
        icon: Users,
        steps: [
            { id: 'research_villages', name: 'Research and visit villages', description: 'Shortlist villages in the area you want, visit them, and ask each for its disclosure statement and contract.', forms: [], estimatedTime: '2-6 weeks', status: 'pending' },
            { id: 'contract_review', name: 'Compare the contracts', description: 'The contract decides what you pay and what comes back. Get the same questions answered for each village, then re-run the village module on the real figures.', forms: ['village_contract_checklist'], estimatedTime: '1-2 weeks', status: 'pending' },
            { id: 'legal_advice', name: 'Legal advice and sign', description: 'Have a solicitor review the contract before you sign, and use the cooling-off period if you need it.', forms: [], estimatedTime: '1-2 weeks', status: 'pending' },
            { id: 'settlement', name: 'Settle and move', description: 'Sell or keep your home as planned, settle, and move in.', forms: ['moving_checklist_village'], estimatedTime: '4-8 weeks', status: 'pending' },
            { id: 'notify_services_australia', name: 'Tell Services Australia', description: 'Report the move and the entry contribution within 14 days — it decides whether you are a homeowner for the pension.', forms: ['village_report'], estimatedTime: 'Within 14 days', status: 'pending' }
        ]
    },
    downsize_home: {
        title: 'Downsizing Journey',
        icon: Home,
        steps: [
            // The downsizer-super decision belongs INSIDE the modelling step (it's one of the
            // strategies compared), not as a later standalone step presuming an answer before the
            // modelling has been done. Its ATO form appears at settlement only when the saved plan
            // makes a contribution.
            { id: 'model_downsize', name: 'Model the Move', description: 'Model the sale, the next home and what happens with the freed-up money — including whether topping up super with the downsizer contribution suits you', forms: [], estimatedTime: '30 minutes', status: 'pending' },
            { id: 'appraise_home', name: 'Appraise Your Home', description: 'Get market appraisals so the modelled sale price reflects reality', forms: [], estimatedTime: '1-2 weeks', status: 'pending' },
            { id: 'find_next_home', name: 'Find the Next Home', description: 'Research and shortlist where you want to live next', forms: [], estimatedTime: '2-8 weeks', status: 'pending' },
            { id: 'confirm_strategy', name: 'Confirm the Strategy', description: 'Revisit the modelling with real appraisal figures and lock in the chosen plan (super contributions, investments, debt)', forms: [], estimatedTime: '1 week', status: 'pending' },
            { id: 'sell_and_settle', name: 'Sell, Settle & Move', description: 'Sale campaign, settlement and the move itself. A downsizer contribution must reach your super fund, with its form, within 90 days of settlement.', forms: ['moving_checklist_sale', 'downsizer_contribution'], estimatedTime: '6-12 weeks', status: 'pending' }
        ]
    },
    granny_flat: {
        title: 'Granny Flat Journey',
        icon: Home,
        steps: [
            { id: 'model_arrangement', name: 'Model the Arrangement', description: 'Model the payment or transfer and its effect on pension and estate', forms: [], estimatedTime: '30 minutes', status: 'pending' },
            { id: 'family_agreement', name: 'Family Agreement', description: 'Agree the arrangement with family — who pays what, and what happens if circumstances change', forms: ['family_guide'], estimatedTime: '1-2 weeks', status: 'pending' },
            { id: 'legal_advice', name: 'Legal Advice', description: 'Put the granny flat interest in writing with independent legal advice — the solicitor brief from the family guide is the starting point', forms: ['solicitor_brief'], estimatedTime: '1-2 weeks', status: 'pending' },
            { id: 'notify_centrelink', name: 'Notify Services Australia', description: 'Call before you set it up, then report it within 14 days so the granny flat interest is assessed correctly', forms: ['granny_flat_report'], estimatedTime: '1 week', status: 'pending' },
            { id: 'build_or_move', name: 'Build / Move In', description: 'Complete any construction and make the move', forms: [], estimatedTime: 'varies', status: 'pending' }
        ]
    }
};

/**
 * A user-saved strategy snapshot (aged care / home care / etc.), persisted to sessionStorage as
 * JSON by the strategy modules — not part of the fact-find schema, so modelled locally. `outputs`
 * covers the fields the Dashboard/WorkflowCard cards actually read; strategy-specific extras pass
 * through the index signature.
 */
export interface SavedStrategy {
    id: string | number;
    type?: string;
    goal?: string;
    name: string;
    createdAt: string | number;
    outputs?: {
        legacyValue?: number;
        legacyEstimate?: number;
        totalCost?: number;
        agedCareCost?: number;
        roomCost?: number;
        annualContribution?: number;
        /** Cashflow & Age Pension plans record all three headline figures, not just the estate —
         *  these levers usually buy a bigger pension by spending capital, so a card showing one
         *  number would flatter the plan. Declared rather than left to the index signature so the
         *  dashboard can render them without a cast. */
        agePension?: number;
        cashFlow?: number;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

/** A saved SOA-generating scenario (case study), persisted to sessionStorage as JSON. */
export interface SavedCaseStudy {
    id: string | number;
    name: string;
    createdAt: string | number;
    caseId?: string;
    soaSource?: string;
    soaSnapshot?: { strategySource?: string; [key: string]: unknown };
    soaDeliveryStatus?: 'draft' | string;
    outputs?: {
        legacyEstimate?: number;
        agedCareCost?: number;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}
